import React from "react";
import { MemberHistory } from "../../types";

interface MemberPointsChartProps {
  histories: MemberHistory[];
  selectedName: string;
}

const W = 300;
const H = 160;
const PAD = { top: 10, right: 12, bottom: 28, left: 38 };
const chartW = W - PAD.left - PAD.right;
const chartH = H - PAD.top - PAD.bottom;

const MemberPointsChart: React.FC<MemberPointsChartProps> = ({
  histories,
  selectedName,
}) => {
  if (histories.length === 0) return null;

  const months = histories[0].data.map((d) => d.month);
  const allPoints = histories.flatMap((h) => h.data.map((d) => d.points));
  const maxPt = Math.max(...allPoints, 1);
  const steps = Math.max(months.length - 1, 1);

  const xPos = (i: number) => PAD.left + (i / steps) * chartW;
  const yPos = (pt: number) => PAD.top + chartH - (pt / maxPt) * chartH;

  const polyline = (data: { month: string; points: number }[]) =>
    data.map((d, i) => `${xPos(i)},${yPos(d.points)}`).join(" ");

  // y-axis grid lines at 0, 25%, 50%, 75%, 100% of maxPt
  const gridYs = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    y: PAD.top + chartH - f * chartH,
    label: Math.round(f * maxPt).toLocaleString(),
  }));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ display: "block", overflow: "visible" }}
    >
      {/* grid lines */}
      {gridYs.map(({ y, label }) => (
        <g key={label}>
          <line
            x1={PAD.left}
            y1={y}
            x2={PAD.left + chartW}
            y2={y}
            stroke="#e2e8f0"
            strokeWidth="0.8"
          />
          <text
            x={PAD.left - 4}
            y={y + 3}
            textAnchor="end"
            fontSize="7"
            fill="#94a3b8"
            fontWeight="600"
          >
            {label}
          </text>
        </g>
      ))}

      {/* x-axis labels */}
      {months.map((m, i) => (
        <text
          key={m}
          x={xPos(i)}
          y={H - 6}
          textAnchor="middle"
          fontSize="7"
          fill="#94a3b8"
          fontWeight="600"
        >
          {m}
        </text>
      ))}

      {/* lines — non-selected first (dimmed), selected on top */}
      {[...histories]
        .sort((a) => (a.name === selectedName ? 1 : -1))
        .map((member) => {
          const isSelected = member.name === selectedName;
          return (
            <g key={member.name}>
              <polyline
                points={polyline(member.data)}
                fill="none"
                stroke={member.color}
                strokeWidth={isSelected ? 2.5 : 1}
                strokeOpacity={isSelected ? 1 : 0.2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {isSelected &&
                member.data.map((d, i) => (
                  <circle
                    key={`${member.name}-${d.month}`}
                    cx={xPos(i)}
                    cy={yPos(d.points)}
                    r="2.5"
                    fill={member.color}
                  />
                ))}
            </g>
          );
        })}
    </svg>
  );
};

export default MemberPointsChart;
